import { CaretDownIcon, GlobeIcon } from '@phosphor-icons/react';
import { Button, Checkbox, Popover, Switch } from '@skupreme/shared/ui';
import { useMarketSelection } from '../../hooks/use-market-selection';
import { countryFlag, MARKET_REGIONS, NO_COUNTRY } from '../../lib/markets';
import {
  byPickerOrder,
  type MarketCount,
  marketName,
} from '../../lib/markets-api';

export type CatalogMarketFilterProps = {
  /** The facet of the current scope and division filter, folded into countries. */
  markets: readonly MarketCount[];
};

const ALL_MARKETPLACES = 'All marketplaces';
const ONLINE_STORES = 'Online stores';
const ALWAYS_SHOWN = 'always shown';

/* "🇺🇸 US" on the trigger; the no-country stores have no flag. */
const shortLabel = (country: string) =>
  country === NO_COUNTRY ? ONLINE_STORES : `${countryFlag(country)} ${country}`;

const withFlag = (country: string) =>
  `${countryFlag(country)} ${marketName(country)}`.trim();

const ROW =
  'sk:flex sk:items-center sk:justify-between sk:gap-3 sk:px-3 sk:py-1.5';

/**
 * M2 (Ely 10-31): the visible country toggle beside the Filters button. A dropdown grouped by region with a tri-state
 * box per region, or a static chip when the scope lists in one country only (a country division, Ely 11-01).
 */
export function CatalogMarketFilter({ markets }: CatalogMarketFilterProps) {
  const {
    countries: selected,
    onlyListed,
    setCountries,
    setOnlyListed,
    clear,
  } = useMarketSelection();
  const items = new Map(markets.map(m => [m.country, m.items]));
  /* A picked country with no items under these divisions stays, at 0. */
  const shown = [
    ...new Set([...markets.map(m => m.country), ...selected]),
  ].sort(byPickerOrder);

  if (shown.length === 0) return null;
  if (shown.length === 1) {
    return (
      <span className="sk:inline-flex sk:h-7 sk:items-center sk:gap-1.5 sk:rounded-full sk:px-3 sk:text-dense sk:text-fg sk:inset-shadow-hairline">
        <span aria-hidden>{countryFlag(shown[0])}</span>
        <span>{marketName(shown[0])}</span>
      </span>
    );
  }

  const isSelected = (country: string) => selected.includes(country);
  /* A country picked keeps the online stores in the list (withNoCountry); their box shows it and cannot drop them. */
  const storesKept = selected.some(c => c !== NO_COUNTRY);
  const keptHere = (country: string) => storesKept && country === NO_COUNTRY;
  const toggle = (countries: readonly string[], on: boolean) =>
    setCountries(
      (on
        ? [...new Set([...selected, ...countries])]
        : selected.filter(c => !countries.includes(c))
      ).sort(byPickerOrder),
    );
  const groups = [
    ...MARKET_REGIONS.map(region => ({
      name: region.name,
      countries: region.countries.filter(c => shown.includes(c)),
      isRegion: true,
    })),
    {
      name: ONLINE_STORES,
      countries: shown.filter(c => c === NO_COUNTRY),
      isRegion: false,
    },
  ].filter(group => group.countries.length > 0);

  const triggerLabel = selected.length
    ? selected.map(shortLabel).join(', ')
    : ALL_MARKETPLACES;

  return (
    <Popover
      aria-label="Marketplaces"
      placement="bottom end"
      trigger={
        <Button
          variant="pill"
          size="md"
          aria-label={`Marketplaces: ${
            selected.length
              ? selected.map(marketName).join(', ')
              : ALL_MARKETPLACES
          }`}
        >
          {selected.length ? null : (
            <GlobeIcon size={14} weight="bold" aria-hidden />
          )}
          <span className="sk:max-w-56 sk:truncate">{triggerLabel}</span>
          <CaretDownIcon size={12} weight="bold" aria-hidden />
        </Button>
      }
    >
      <div className="sk:flex sk:w-72 sk:flex-col">
        <div className="sk:border-b sk:border-border-subtle sk:px-1 sk:pb-1">
          <Button variant="menu" onPress={clear}>
            <GlobeIcon size={14} aria-hidden />
            {ALL_MARKETPLACES}
          </Button>
        </div>
        <div className="sk:max-h-96 sk:overflow-y-auto sk:py-1">
          {groups.map(group => {
            const picked = group.countries.filter(isSelected).length;
            return (
              <fieldset
                key={group.name}
                aria-label={group.name}
                className="sk:m-0 sk:min-w-0 sk:border-0 sk:p-0"
              >
                <div className={`${ROW} sk:pt-2`}>
                  {group.isRegion ? (
                    <Checkbox
                      aria-label={`All of ${group.name}`}
                      isSelected={picked === group.countries.length}
                      isIndeterminate={
                        picked > 0 && picked < group.countries.length
                      }
                      onChange={on => toggle(group.countries, on)}
                    >
                      <span className="sk:text-xs sk:font-semibold sk:text-fg-tertiary">
                        {group.name}
                      </span>
                    </Checkbox>
                  ) : (
                    <span className="sk:text-xs sk:font-semibold sk:text-fg-tertiary">
                      {group.name}
                    </span>
                  )}
                </div>
                {group.countries.map(country => {
                  const count = items.get(country) ?? 0;
                  return (
                    <div
                      key={country}
                      className={`${ROW} ${group.isRegion ? 'sk:pl-6' : ''}`}
                    >
                      <Checkbox
                        aria-label={`${marketName(country)}, ${count.toLocaleString()} items${keptHere(country) ? `, ${ALWAYS_SHOWN}` : ''}`}
                        isSelected={isSelected(country) || keptHere(country)}
                        isDisabled={keptHere(country)}
                        onChange={on => toggle([country], on)}
                      >
                        <span aria-hidden>{withFlag(country)}</span>
                        {keptHere(country) ? (
                          <span
                            aria-hidden
                            className="sk:ml-1.5 sk:text-xs sk:text-fg-tertiary"
                          >
                            {ALWAYS_SHOWN}
                          </span>
                        ) : null}
                      </Checkbox>
                      <span
                        aria-hidden
                        className="sk:text-xs sk:tabular-nums sk:text-fg-tertiary"
                      >
                        {count.toLocaleString()}
                      </span>
                    </div>
                  );
                })}
              </fieldset>
            );
          })}
        </div>
        {selected.length ? (
          <div className="sk:border-t sk:border-border-subtle sk:px-3 sk:py-2">
            <Switch isSelected={onlyListed} onChange={setOnlyListed}>
              Only items listed there
            </Switch>
          </div>
        ) : null}
      </div>
    </Popover>
  );
}
